/**
 * Aspect Admin — demo dataset powering the admin portal views.
 * Deterministic, in-memory; replace with real API calls when server lands.
 */

export const WORKSPACE = {
  name: "Northwind Estates",
  plan: "Scale",
  env: "Production",
  region: "eu-west-1",
};

export const KPIS = [
  { id: "spaces", label: "Active spaces", value: "128", delta: 12.4, dir: "up", note: "vs last 30 days", icon: "spaces", spark: [4, 6, 5, 8, 7, 9, 11, 10, 13, 14, 13, 16] },
  { id: "tours", label: "Tour sessions", value: "24,381", delta: 8.1, dir: "up", note: "vs last 30 days", icon: "activity", spark: [10, 12, 9, 14, 13, 15, 14, 18, 16, 19, 21, 22] },
  { id: "jobs", label: "AI generations", value: "1,904", delta: 23.6, dir: "up", note: "vs last 30 days", icon: "sparkles", spark: [3, 4, 6, 5, 8, 9, 8, 12, 11, 14, 15, 17] },
  { id: "latency", label: "Median render time", value: "3.2s", delta: -14.9, dir: "down", good: true, note: "faster than last month", icon: "bolt", spark: [16, 15, 14, 15, 13, 12, 12, 11, 10, 10, 9, 9] },
];

export const TREND = {
  labels: ["May 24", "May 31", "Jun 7", "Jun 14", "Jun 21", "Jun 28", "Jul 4"],
  series: [
    { name: "Tour sessions", color: "#00a8d2", points: [2400, 2850, 2620, 3180, 3420, 3890, 4210] },
    { name: "AI generations", color: "#8fa6c9", points: [420, 510, 480, 640, 720, 810, 930] },
  ],
};

export const USAGE_DONUT = [
  { label: "Staging renders", value: 46, color: "#00a8d2" },
  { label: "Plan analysis", value: 27, color: "#55c9e8" },
  { label: "Tour exports", value: 17, color: "#9bb8cc" },
  { label: "Other", value: 10, color: "#d5e3ea" },
];

export const INSIGHTS = [
  {
    tag: "Buyer attention",
    title: "Kitchen & living room hold 61% of viewing time",
    body: "Buyers dwell longest in the kitchen and living room, while bathrooms get skimmed in under 8s. Staged rooms hold attention 2.3× longer than empty ones — stage these two spaces first.",
    conf: 3,
    action: "View analytics",
    route: "/analytics",
  },
  {
    tag: "Viewing time",
    title: "Most tours are watched at night, 20:00–23:00",
    body: "44% of sessions start after 20:00, when buyers browse from the couch. Evening viewers stay 38% longer and are 2× more likely to request an in-person viewing the next day.",
    conf: 3,
    action: "See sessions",
    route: "/analytics",
  },
  {
    tag: "What sells",
    title: "Bright, naturally-lit rooms drive the most zoom-ins",
    body: "Daytime-lit renders get 27% more zoom-ins and hotspot clicks than dim shots. Warm evening lighting converts best for bedrooms — mixed lighting keeps buyers exploring longer.",
    conf: 2,
    action: "Open analytics",
    route: "/analytics",
  },
  {
    tag: "Drop-off",
    title: "68% exit before the second bedroom",
    body: "Most buyers leave the tour after the living area — they never reach secondary bedrooms. Adding a guided hotspot path through the hallway lifts completion rate by 22%.",
    conf: 2,
    action: "View heatmap",
    route: "/analytics",
  },
  {
    tag: "Mobile",
    title: "72% of tours open on a phone",
    body: "Mobile viewers spend less time per room but return 1.8× more often. Vertical pan in narrow hallways and large tap targets on hotspots keep phone sessions from dropping early.",
    conf: 3,
    action: "See devices",
    route: "/analytics",
  },
  {
    tag: "Return visits",
    title: "Repeat viewers are 4× more likely to book a viewing",
    body: "Buyers who open the same tour twice within 7 days almost always schedule an in-person visit. Send a follow-up link 48h after the first session to catch warm leads.",
    conf: 3,
    action: "View leads",
    route: "/clients",
  },
];

export const JOBS = [
  { id: "job_9f3k", type: "Staging render", space: "Riverside Loft 4B", client: "Meridian Group", status: "running", progress: 72, model: "stage-v4", eta: "42s", started: "2m ago", conf: null },
  { id: "job_8u1c", type: "Plan analysis", space: "Kastani 14 · Unit 7", client: "Baltic Homes", status: "running", progress: 31, model: "vision-v2", eta: "1m 50s", started: "40s ago", conf: null },
  { id: "job_7pa2", type: "Tour export", space: "Marina Tower 12F", client: "Meridian Group", status: "queued", progress: 0, model: "export-v1", eta: "queued #3", started: "—", conf: null },
  { id: "job_6mm0", type: "Staging render", space: "Old Town Attic", client: "Havencrest", status: "queued", progress: 0, model: "stage-v4", eta: "queued #4", started: "—", conf: null },
  { id: "job_5xw8", type: "Plan analysis", space: "Pärnu Rd 22 · 3BR", client: "Baltic Homes", status: "done", progress: 100, model: "vision-v2", eta: "—", started: "18m ago", conf: 0.94 },
  { id: "job_4hh1", type: "Staging render", space: "Seaside Villa", client: "Havencrest", status: "done", progress: 100, model: "stage-v4", eta: "—", started: "26m ago", conf: 0.89 },
  { id: "job_3rt9", type: "Restyle · Scandinavian", space: "Marina Tower 12F", client: "Meridian Group", status: "done", progress: 100, model: "stage-v4", eta: "—", started: "1h ago", conf: 0.91 },
  { id: "job_2qd4", type: "Plan analysis", space: "Warehouse Loft", client: "Atelier North", status: "failed", progress: 100, model: "vision-v2", eta: "—", started: "2h ago", conf: 0.41, error: "Low-resolution source image (640×420). Confidence below 0.5 threshold." },
  { id: "job_1zz7", type: "Tour export", space: "Garden Duplex", client: "Havencrest", status: "done", progress: 100, model: "export-v1", eta: "—", started: "3h ago", conf: null },
];

export const QUEUE_HEALTH = {
  depth: 4,
  workers: "6 / 8",
  p95: "5.8s",
  successRate: 98.2,
};

export const ACTIVITY = [
  { icon: "sparkles", kind: "", who: "AI Engine", text: "<strong>Staging render</strong> completed for <strong>Seaside Villa</strong> · 24 items placed, confidence 0.89", time: "26m" },
  { icon: "upload", kind: "", who: "Karin Vaher", text: "<strong>Karin Vaher</strong> uploaded 3 floor plans to <strong>Kastani 14</strong>", time: "38m" },
  { icon: "check", kind: "ok", who: "System", text: "Tour <strong>Marina Tower 12F</strong> published — link active", time: "1h" },
  { icon: "users", kind: "", who: "Admin", text: "<strong>Tomas Berzins</strong> invited <strong>l.kask@meridian.ee</strong> as Editor", time: "2h" },
  { icon: "alert", kind: "warn", who: "System", text: "Plan analysis for <strong>Warehouse Loft</strong> flagged low confidence (0.41)", time: "2h" },
  { icon: "card", kind: "", who: "Billing", text: "Invoice <strong>INV-2026-0142</strong> paid — €1,240.00", time: "1d" },
];

export const SPACES = [
  { id: "sp_01", name: "Riverside Loft 4B", client: "Meridian Group", status: "rendering", rooms: 5, area: 96, tours: 1240, updated: "2m ago", owner: "KV" },
  { id: "sp_02", name: "Marina Tower 12F", client: "Meridian Group", status: "live", rooms: 4, area: 88, tours: 3182, updated: "1h ago", owner: "TB" },
  { id: "sp_03", name: "Kastani 14 · Unit 7", client: "Baltic Homes", status: "analyzing", rooms: 3, area: 64, tours: 0, updated: "40s ago", owner: "KV" },
  { id: "sp_04", name: "Old Town Attic", client: "Havencrest", status: "draft", rooms: 2, area: 47, tours: 0, updated: "3d ago", owner: "ML" },
  { id: "sp_05", name: "Seaside Villa", client: "Havencrest", status: "live", rooms: 7, area: 210, tours: 5920, updated: "26m ago", owner: "TB" },
  { id: "sp_06", name: "Garden Duplex", client: "Havencrest", status: "live", rooms: 5, area: 132, tours: 1877, updated: "3h ago", owner: "ML" },
  { id: "sp_07", name: "Pärnu Rd 22 · 3BR", client: "Baltic Homes", status: "draft", rooms: 3, area: 71, tours: 0, updated: "18m ago", owner: "KV" },
  { id: "sp_08", name: "Warehouse Loft", client: "Atelier North", status: "attention", rooms: 4, area: 148, tours: 312, updated: "2h ago", owner: "AN" },
];

export const CLIENTS = [
  { id: "cl_01", name: "Meridian Group", contact: "Liis Kask", email: "l.kask@meridian.ee", spaces: 34, seats: 6, mrr: "€490", status: "active", since: "Mar 2024", health: 92 },
  { id: "cl_02", name: "Havencrest", contact: "Marta Liiv", email: "marta@havencrest.com", spaces: 41, seats: 8, mrr: "€690", status: "active", since: "Jan 2024", health: 88 },
  { id: "cl_03", name: "Baltic Homes", contact: "Jaan Tamm", email: "jaan@baltichomes.ee", spaces: 22, seats: 4, mrr: "€290", status: "active", since: "Sep 2024", health: 74 },
  { id: "cl_04", name: "Atelier North", contact: "Anna Nord", email: "anna@ateliernorth.io", spaces: 9, seats: 2, mrr: "€120", status: "trial", since: "Jun 2026", health: 61 },
  { id: "cl_05", name: "Vela Properties", contact: "Piotr Mazur", email: "piotr@vela.pl", spaces: 18, seats: 3, mrr: "€290", status: "active", since: "Nov 2024", health: 83 },
  { id: "cl_06", name: "Solhem Bostad", contact: "Elsa Lind", email: "elsa@solhem.se", spaces: 4, seats: 2, mrr: "—", status: "churn-risk", since: "Feb 2025", health: 38 },
];

export const TEAM = [
  { id: "u_01", name: "Tomas Berzins", email: "tomas@aspect.app", role: "Owner", status: "active", last: "Now", twofa: true },
  { id: "u_02", name: "Karin Vaher", email: "karin@aspect.app", role: "Admin", status: "active", last: "12m ago", twofa: true },
  { id: "u_03", name: "Marta Liiv", email: "marta@aspect.app", role: "Editor", status: "active", last: "1h ago", twofa: true },
  { id: "u_04", name: "Anders Holm", email: "anders@aspect.app", role: "Editor", status: "active", last: "Yesterday", twofa: false },
  { id: "u_05", name: "Liis Kask", email: "l.kask@meridian.ee", role: "Viewer", status: "invited", last: "—", twofa: false },
];

export const ROLES = ["Owner", "Admin", "Editor", "Viewer"];

export const PERMISSIONS = [
  { name: "View spaces & tours", grants: [1, 1, 1, 1] },
  { name: "Create & edit spaces", grants: [1, 1, 1, 0] },
  { name: "Run AI generations", grants: [1, 1, 1, 0] },
  { name: "Publish tours", grants: [1, 1, 0, 0] },
  { name: "Manage clients", grants: [1, 1, 0, 0] },
  { name: "Invite members", grants: [1, 1, 0, 0] },
  { name: "Billing & invoices", grants: [1, 0, 0, 0] },
  { name: "API keys & webhooks", grants: [1, 1, 0, 0] },
  { name: "Workspace settings", grants: [1, 0, 0, 0] },
];

export const INVOICES = [
  { id: "INV-2026-0142", date: "Jul 1, 2026", amount: "€1,240.00", status: "paid" },
  { id: "INV-2026-0128", date: "Jun 1, 2026", amount: "€1,240.00", status: "paid" },
  { id: "INV-2026-0114", date: "May 1, 2026", amount: "€1,180.00", status: "paid" },
  { id: "INV-2026-0101", date: "Apr 1, 2026", amount: "€1,180.00", status: "paid" },
  { id: "INV-2026-0087", date: "Mar 1, 2026", amount: "€980.00", status: "paid" },
];

export const USAGE_CAPS = [
  { label: "AI generations", used: 1904, cap: 3000, unit: "" },
  { label: "Published tours", used: 86, cap: 150, unit: "" },
  { label: "Storage", used: 412, cap: 1024, unit: "GB" },
  { label: "Team seats", used: 5, cap: 10, unit: "" },
];

export const INTEGRATIONS = [
  { id: "slack", name: "Slack", initial: "S", desc: "Job completions, publish events, and AI alerts in your channels.", connected: true },
  { id: "webflow", name: "Webflow", initial: "W", desc: "Embed published tours directly into client-facing listing pages.", connected: true },
  { id: "hubspot", name: "HubSpot", initial: "H", desc: "Sync client organizations and tour engagement to CRM contacts.", connected: false },
  { id: "zapier", name: "Zapier", initial: "Z", desc: "Automate workflows across 6,000+ apps when portal events fire.", connected: false },
  { id: "drive", name: "Google Drive", initial: "G", desc: "Import floor plans and export tour assets to shared drives.", connected: true },
  { id: "matterport", name: "Matterport", initial: "M", desc: "Pull existing scans as base geometry for AI staging.", connected: false },
];

export const API_KEYS = [
  { id: "key_live", label: "Production key", prefix: "asp_live_", masked: "asp_live_9f2•••••••••••kQ3d", created: "Mar 12, 2026", lastUsed: "4m ago", scopes: "Full access" },
  { id: "key_test", label: "Staging key", prefix: "asp_test_", masked: "asp_test_2ba•••••••••••x91M", created: "Mar 12, 2026", lastUsed: "2d ago", scopes: "Read + jobs" },
];

export const WEBHOOKS = [
  { id: "wh_1", url: "https://api.meridian.ee/hooks/aspect", events: "job.completed, tour.published", status: "active", success: 99.4 },
  { id: "wh_2", url: "https://hooks.slack.com/services/T02…", events: "job.failed", status: "active", success: 100 },
  { id: "wh_3", url: "https://staging.havencrest.com/wh", events: "space.updated", status: "paused", success: 87.1 },
];

export const NOTIFICATIONS = [
  { id: "n1", icon: "sparkles", kind: "", title: "Staging render completed", body: "Seaside Villa — 24 items placed with 0.89 confidence.", time: "26m", unread: true, group: "Today" },
  { id: "n2", icon: "alert", kind: "warn", title: "Low confidence analysis", body: "Warehouse Loft plan scored 0.41 — a higher-resolution image is recommended.", time: "2h", unread: true, group: "Today" },
  { id: "n3", icon: "users", kind: "", title: "Invitation accepted", body: "Marta Liiv joined the workspace as Editor.", time: "5h", unread: true, group: "Today" },
  { id: "n4", icon: "card", kind: "ok", title: "Payment received", body: "Invoice INV-2026-0142 (€1,240.00) was paid via SEPA.", time: "1d", unread: false, group: "Yesterday" },
  { id: "n5", icon: "bolt", kind: "", title: "Queue autoscaled", body: "Worker pool expanded 4 → 8 to absorb the Meridian batch upload.", time: "1d", unread: false, group: "Yesterday" },
  { id: "n6", icon: "shield", kind: "", title: "New sign-in", body: "Tomas Berzins signed in from Tallinn, EE (Chrome / macOS).", time: "2d", unread: false, group: "Earlier" },
];

export const FILES = [
  { id: "f1", name: "riverside-loft-plan-v3.png", type: "Floor plan", size: "4.2 MB", space: "Riverside Loft 4B", uploaded: "2h ago", by: "Karin Vaher", kind: "image" },
  { id: "f2", name: "marina-tower-hero.jpg", type: "Photo", size: "8.1 MB", space: "Marina Tower 12F", uploaded: "1d ago", by: "Tomas Berzins", kind: "image" },
  { id: "f3", name: "seaside-villa-tour.glb", type: "3D export", size: "36.4 MB", space: "Seaside Villa", uploaded: "1d ago", by: "AI Engine", kind: "cube" },
  { id: "f4", name: "kastani-14-blueprints.pdf", type: "Document", size: "12.7 MB", space: "Kastani 14 · Unit 7", uploaded: "2d ago", by: "Jaan Tamm", kind: "doc" },
  { id: "f5", name: "brand-watermark.svg", type: "Brand asset", size: "18 KB", space: "—", uploaded: "1w ago", by: "Karin Vaher", kind: "image" },
  { id: "f6", name: "garden-duplex-plan.dwg", type: "CAD file", size: "6.9 MB", space: "Garden Duplex", uploaded: "2w ago", by: "Marta Liiv", kind: "doc" },
];

export const AUDIT = [
  { id: "a1", actor: "Tomas Berzins", action: "settings.updated", detail: "Changed default tour visibility: private → link-only", ip: "82.131.24.7", time: "Today 09:42", sev: "info" },
  { id: "a2", actor: "AI Engine", action: "job.failed", detail: "Plan analysis job_2qd4 — confidence 0.41 below threshold", ip: "internal", time: "Today 09:18", sev: "warn" },
  { id: "a3", actor: "Karin Vaher", action: "apikey.rotated", detail: "Rotated staging key asp_test_2ba…", ip: "82.131.24.9", time: "Yesterday 17:03", sev: "info" },
  { id: "a4", actor: "System", action: "queue.autoscale", detail: "Worker pool 4 → 8 (load threshold exceeded)", ip: "internal", time: "Yesterday 09:40", sev: "info" },
  { id: "a5", actor: "Tomas Berzins", action: "member.invited", detail: "Invited l.kask@meridian.ee with role Viewer", ip: "82.131.24.7", time: "Jul 2 14:22", sev: "info" },
  { id: "a6", actor: "Unknown", action: "auth.failed", detail: "3 failed sign-in attempts for anders@aspect.app", ip: "45.90.12.201", time: "Jul 2 03:11", sev: "err" },
  { id: "a7", actor: "Marta Liiv", action: "tour.published", detail: "Published Marina Tower 12F (link-only)", ip: "88.196.55.14", time: "Jul 1 16:48", sev: "info" },
  { id: "a8", actor: "Billing", action: "invoice.paid", detail: "INV-2026-0142 · €1,240.00 via SEPA", ip: "internal", time: "Jul 1 08:00", sev: "ok" },
];

export const TICKETS = [
  { id: "t1", subject: "Textures look washed out on exported tours", from: "Baltic Homes", status: "open", priority: "high", updated: "1h ago", assignee: "Karin Vaher" },
  { id: "t2", subject: "Request: bulk CSV import for space metadata", from: "Meridian Group", status: "open", priority: "medium", updated: "6h ago", assignee: "—" },
  { id: "t3", subject: "Tour link 404s after re-publish", from: "Havencrest", status: "pending", priority: "high", updated: "1d ago", assignee: "Tomas Berzins" },
  { id: "t4", subject: "How to add custom watermark per client?", from: "Vela Properties", status: "resolved", priority: "low", updated: "3d ago", assignee: "Marta Liiv" },
];

export const ANALYTICS_TOP_SPACES = [
  { name: "Seaside Villa", client: "Havencrest", sessions: 5920, avg: "4m 12s", completion: 76 },
  { name: "Marina Tower 12F", client: "Meridian Group", sessions: 3182, avg: "3m 38s", completion: 71 },
  { name: "Garden Duplex", client: "Havencrest", sessions: 1877, avg: "2m 54s", completion: 64 },
  { name: "Riverside Loft 4B", client: "Meridian Group", sessions: 1240, avg: "3m 05s", completion: 68 },
  { name: "Warehouse Loft", client: "Atelier North", sessions: 312, avg: "1m 47s", completion: 41 },
];

export const DEVICE_SPLIT = [
  { label: "Desktop", value: 54, color: "#00a8d2" },
  { label: "Mobile", value: 38, color: "#55c9e8" },
  { label: "Tablet", value: 8, color: "#d5e3ea" },
];
